import styled from "styled-components";
import {memo, useRef, useState} from "react";
import {Observer} from "mobx-react";
import {baseDistance, radius} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import {XyiconFeature} from "../../../../generated/api/base";
import {Functions} from "../../../../utils/function/Functions";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {useAppStore} from "../../../../StateManager";
import {FilterEditorV5} from "../../filter/FilterEditorV5";
import type {SpaceViewRenderer} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import {useClickOutside, useCloseOnKeyboardPress} from "../../utils";
import {SpotlightBodyContentV5} from "../../spotlight/SpotlightContentV5";
import {LayersPanelV5} from "./viewbar/LayersPanelV5";
import {CaptionPanelV5} from "./viewbar/CaptionPanelV5";

interface IOverlayPanelProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly openedSpaceEditorPanelType: TSpaceEditorPanelTypes | null;
	readonly onConfiguratorOpenChange?: (isOpen: boolean) => void;
	readonly onClose?: () => void;
}

const OverlayPanel = memo((props: IOverlayPanelProps) => {
	return (
		<Observer>
			{() => {
				const {spaceViewRenderer, openedSpaceEditorPanelType, onConfiguratorOpenChange, onClose} = props;
				const filteredItems = spaceViewRenderer.spaceItemController.getFilteredItems();
				const selectedView = spaceViewRenderer.actions.getSelectedView(XyiconFeature.SpaceEditor);

				switch (openedSpaceEditorPanelType) {
					case "filter":
						return (
							<FilterEditorV5
								features={[XyiconFeature.Xyicon, XyiconFeature.Boundary]}
								feature={XyiconFeature.SpaceEditor}
								items={filteredItems}
								onSelect={Functions.emptyFunction}
								isSpaceEditor={true}
								onClose={onClose}
							/>
						);
					case "layers":
						return (
							<LayersPanelV5
								spaceViewRenderer={spaceViewRenderer}
								showNumbers={true}
								view={selectedView}
								onClose={onClose}
							/>
						);
					case "labels":
						return (
							<CaptionPanelV5
								spaceViewRenderer={spaceViewRenderer}
								view={selectedView}
								onConfiguratorOpenChange={onConfiguratorOpenChange}
								onClose={onClose}
							/>
						);
					default:
						return null;
				}
			}}
		</Observer>
	);
});

export type TSpaceEditorPanelTypes = "spotlight" | "layers" | "labels" | "filter";

interface ISpaceEditorPanelV5Props {
	readonly openedSpaceEditorPanelType: TSpaceEditorPanelTypes | null;
	readonly onClose: () => void;
}

export const SpaceEditorPanelV5 = (props: ISpaceEditorPanelV5Props) => {
	const appState = useAppStore((state) => state.appState);
	const {openedSpaceEditorPanelType, onClose} = props;
	const {spaceViewRenderer} = appState.app;
	const ref = useRef<HTMLDivElement>(null);
	const [isConfiguratorOpen, setIsConfiguratorOpen] = useState<boolean>(false);

	useClickOutside(
		[ref],
		() => {
			if (!isConfiguratorOpen) {
				onClose();
			}
		},
		"mousedown",
	);
	useCloseOnKeyboardPress(onClose, KeyboardListener.KEY_ESCAPE, [!!openedSpaceEditorPanelType, isConfiguratorOpen], {
		enabled: !!openedSpaceEditorPanelType && !isConfiguratorOpen,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	return (
		<Observer>
			{() => {
				const {actions} = spaceViewRenderer.transport.appState;
				const selectedView = spaceViewRenderer.actions.getSelectedView(XyiconFeature.SpaceEditor);
				const isModelInSpace = (model: any) => model.spaceId === spaceViewRenderer.space?.id;
				const boundaries = actions.getList(XyiconFeature.Boundary).filter((boundary: any) => [...boundary.boundarySpaceMaps].some(isModelInSpace));
				const xyicons = actions.getList(XyiconFeature.Xyicon).filter(isModelInSpace);
				const allItems = [...boundaries, ...xyicons];

				const isSpotlight = openedSpaceEditorPanelType === "spotlight";
				const isOpen = !!openedSpaceEditorPanelType;
				const isAdvanced = openedSpaceEditorPanelType === "filter" && selectedView.filters.type === "advanced";

				return (
					<>
						<SpotlightEditorPanelStyled
							$isOpen={isSpotlight && isOpen}
							ref={isSpotlight ? ref : null}
						>
							{isSpotlight && (
								<SpotlightBodyContentV5
									feature={XyiconFeature.SpaceEditor}
									items={allItems}
									openInSpaceEditor={true}
								/>
							)}
						</SpotlightEditorPanelStyled>
						<SpaceEditorPanelContainerStyled
							$isOpen={!isSpotlight && isOpen}
							$isAdvanced={isAdvanced}
							ref={!isSpotlight ? ref : null}
						>
							{!isSpotlight && (
								<OverlayPanel
									spaceViewRenderer={spaceViewRenderer}
									openedSpaceEditorPanelType={openedSpaceEditorPanelType}
									onConfiguratorOpenChange={setIsConfiguratorOpen}
									onClose={onClose}
								/>
							)}
						</SpaceEditorPanelContainerStyled>
					</>
				);
			}}
		</Observer>
	);
};

const SpaceEditorPanelContainerStyled = styled.div<{$isOpen: boolean; $isAdvanced: boolean}>`
	display: flex;
	flex-direction: column;
	min-height: 0;
	width: 408px;
	position: absolute;
	right: 16px;
	top: 76px;
	height: calc(100vh - 163px);
	border-radius: ${radius.xl};
	background-color: ${colorPalette.white};
	z-index: 9;
	box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.5);
	padding: ${baseDistance.sm};
	gap: ${baseDistance.sm};
	transition:
		transform 0.2s ease-in-out,
		opacity 0.2s ease-in-out;
	transform: translateX(calc(100% + 16px));
	opacity: 0;
	pointer-events: none;

	${(props) => {
		if (props.$isAdvanced && props.$isOpen) {
			return `
				width: 652px;
				top: 68px;
				bottom: unset;
				height: auto;
				max-height: calc(100vh - 163px);
				transform: translateX(0);
				opacity: 1;
				pointer-events: auto;
			`;
		}

		if (props.$isOpen) {
			return `
				transform: translateX(0);
				opacity: 1;
				pointer-events: auto;
			`;
		}
		return `
			transform: translateX(calc(100% + 16px));
			opacity: 0;
			pointer-events: none;
		`;
	}}
`;

const SpotlightEditorPanelStyled = styled.div<{$isOpen: boolean}>`
	display: flex;
	flex-direction: column;
	max-width: 660px;
	width: 100%;
	position: absolute;
	right: 16px;
	top: 76px;
	min-height: 192px;
	max-height: 592px;
	border-radius: ${radius.md};
	background-color: ${colorPalette.white};
	z-index: 9;
	box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.5);
	gap: ${baseDistance.md};
	transition:
		transform 0.2s ease-in-out,
		opacity 0.2s ease-in-out;
	transform: translateX(calc(100% + 16px));
	opacity: 0;
	pointer-events: none;

	.field-rule-title {
		max-width: 464px;
	}

	${(props) => {
		if (props.$isOpen) {
			return `
				transform: translateX(0);
				opacity: 1;
				pointer-events: auto;
			`;
		}
		return `
			transform: translateX(calc(100% + 16px));
			opacity: 0;
			pointer-events: none;
		`;
	}}
`;
